import ArrayUtils from "../utils/ArrayUtils.js";
import ScoreUtils from "../utils/ScoreUtils.js";
import StorageService from "../services/StorageService.js";
import SoundManager from "../utils/SoundManager.js";
import FeedbackEffects from "../utils/FeedbackEffects.js";
import AccountSync from "../services/AccountSync.js";
import * as FlagPreloader from "../utils/FlagPreloader.js";
import { saveWeekRun } from "../utils/WeekRun.js";
import * as Passport from "../utils/Passport.js";
import * as ReviewDeck from "../utils/ReviewDeck.js";
import { renderAccountNudge, NUDGE_MIN_SCORE } from "../utils/AccountNudge.js";
import { bindAnswerKeys } from "../utils/QuizKeyboard.js";
import { renderErrorsRecap } from "../utils/ErrorsRecap.js";
import { enterEndGame } from "../utils/EndGameUI.js";
import { showConfirmation } from "../utils/ConfirmationPopup.js";

export default class BaseSpeedRun {
    constructor(gameId, pageSelector, config = {}) {
        this.gameId = gameId;
        // Sharing / end-screen metadata: { gameNameKey, modeLabelKey, slug }.
        this.config = config;
        this.page = document.querySelector(pageSelector);
        if (!this.page) return;

        this.flagContainer = this.page.querySelector(".flag-container");
        this.answerContainer = this.page.querySelector(".answer-container");
        this.winMessage = this.page.querySelector(".loose-message");
        this.timeDisplay = this.page.querySelector(".time-value");
        this.timerBar = this.page.querySelector(".timer-bar-inner");
        this.progressContainer = this.page.querySelector(".progress-container");
        this.timerContainer = this.page.querySelector(".timer-container");
        this.titleContainer = this.page.querySelector(".title-container");

        this.timeLeft = 60;
        this.score = 0;
        this.gameActive = false;
        this.ended = false;
        this.isAnswering = false;
        this.previousBest = 0;
        this.isNewBest = false;
        this.timer = null;
        this.errors = [];
    }

    async init() {
        this.reloadButton();
        this.bindRestartButton();
        this.initShare();
        this.resetGame();
        this.awaitStart();
    }

    /**
     * Attend un départ explicite du joueur.
     *
     * Le chrono partait à l'ouverture de la page, pendant le chargement de la
     * première question et la lecture de la consigne : sur un mode classé, les
     * premières secondes étaient perdues sans que personne ne les ait jouées. Le
     * bouton « Rejouer » de l'écran de fin, lui, relance directement : le clic
     * vaut déjà départ.
     */
    awaitStart() {
        const overlay = this.page.querySelector(".speedrun-start");
        const startBtn = overlay && overlay.querySelector(".btn-start-run");
        if (!overlay || !startBtn) {
            this.startGame();
            return;
        }

        // Masqué tant que l'écran de démarrage est affiché : cliquer avant que
        // la partie n'ait commencé lancerait le jeu sous la popup restée à l'écran.
        const restartButton = this.page.querySelector(".btn-restart-ingame");
        if (restartButton) restartButton.style.display = "none";

        overlay.classList.add("show");
        startBtn.addEventListener("click", () => {
            const go = () => {
                overlay.classList.remove("show");
                if (restartButton) restartButton.style.display = "";
                this.startGame();
            };
            // Les premiers drapeaux se chargent pendant la lecture de la consigne ;
            // sur une connexion lente, le départ attend qu'ils soient là.
            if (this.firstFlagsLoaded) { go(); return; }
            startBtn.disabled = true;
            this.firstFlagsReady.then(() => {
                startBtn.disabled = false;
                go();
            });
        }, { once: true });
    }

    resetGame() {
        this.timeLeft = 60;
        this.score = 0;
        this.errors = [];
        this.gameActive = true;
        this.ended = false;
        this.pool = ArrayUtils.shuffle([...geoNerdApp.countries]);
        this.poolIndex = 0;
        // Une question en attente de son drapeau ne doit pas s'afficher dans la
        // partie suivante.
        this.questionToken = (this.questionToken || 0) + 1;
        this.waitingForFlag = false;
        this.firstFlagsReady = this.preloadPoolFlags();
        if (this.winMessage) this.winMessage.classList.remove("show");
        if (this.titleContainer) this.titleContainer.style.display = "";

        const uiElements = [this.flagContainer, this.answerContainer, this.progressContainer, this.timerContainer];
        uiElements.forEach(el => {
            if (el) {
                el.style.display = (el === this.answerContainer) ? "grid" : "flex";
                el.style.opacity = "1";
            }
        });
        const restartButton = this.page.querySelector(".btn-restart-ingame");
        if (restartButton) restartButton.style.display = "";

        this.updateProgress();
        this.updateTimerDisplay();
    }

    /**
     * L'ordre des questions est connu d'avance : les drapeaux se chargent dans cet
     * ordre, et seuls les premiers sont attendus avant le départ.
     */
    preloadPoolFlags() {
        const codes = this.pool.flatMap(country => this.getQuestionFlagCodes(country));
        const first = codes.slice(0, 6);
        if (FlagPreloader.isActive()) FlagPreloader.prioritize(codes);
        this.firstFlagsLoaded = !FlagPreloader.isActive() || FlagPreloader.areLoaded(first);
        if (this.firstFlagsLoaded) return Promise.resolve();
        return FlagPreloader.load(first).then(() => { this.firstFlagsLoaded = true; });
    }

    startGame() {
        this.stopTimer();
        this.timer = setInterval(() => {
            // Chrono suspendu tant que le drapeau de la question n'est pas arrivé :
            // une image lente ne doit pas coûter de temps sur un mode classé.
            if (this.waitingForFlag) return;
            this.timeLeft--;
            this.updateTimerDisplay();
            if (this.timeLeft <= 0) {
                this.endGame();
            }
        }, 1000);
        this.nextQuestion();
    }

    stopTimer() {
        if (this.timer) {
            clearInterval(this.timer);
            this.timer = null;
        }
    }

    updateTimerDisplay() {
        if (!this.timeDisplay || !this.timerBar) return;

        this.timeDisplay.textContent = this.timeLeft;
        const percentage = (this.timeLeft / 60) * 100;
        this.timerBar.style.width = percentage + "%";
        this.timerBar.style.backgroundColor = this.timeLeft <= 10 ? "#F05050" : "#a855f7";
    }

    updateProgress() {
        const foundEl = this.page.querySelector(".progress .found");
        const bestEl = this.page.querySelector(".progress .best .value");
        if (foundEl) foundEl.textContent = this.score;
        if (bestEl) bestEl.textContent = StorageService.load(`${this.gameId}.best`, 0);
    }

    /**
     * Fin de partie. Idempotente : le chrono peut tomber à 0 par deux chemins
     * (le tick, ou la pénalité de -2s d'une mauvaise réponse), et `saveStats()`
     * incrémente gamesPlayed/cumulativeScore — un double appel fausserait les
     * stats cumulées.
     */
    endGame() {
        if (this.ended) return;
        this.ended = true;
        this.gameActive = false;
        this.stopTimer();
        SoundManager.gameOver();

        this.saveStats(this.score);
        enterEndGame(this.page);
        this.renderEndGameStats(this.winMessage);
        this.renderErrors(this.winMessage);

        if (this.flagContainer) this.flagContainer.style.display = "none";
        if (this.answerContainer) this.answerContainer.style.display = "none";
        if (this.progressContainer) this.progressContainer.style.display = "none";
        if (this.timerContainer) this.timerContainer.style.display = "none";
        if (this.titleContainer) this.titleContainer.style.display = "none";
        const restartButton = this.page.querySelector(".btn-restart-ingame");
        if (restartButton) restartButton.style.display = "none";
        if (this.winMessage) this.winMessage.classList.add("show");
    }

    reloadButton() {
        this.page.querySelectorAll(".reload").forEach(button => {
            button.addEventListener("click", () => {
                this.resetGame();
                this.startGame();
            });
        });
    }

    /**
     * Wires the in-game restart button: relaunches the 60s clock without
     * counting the abandoned run in the stats (`endGame()` is what saves
     * them, and it's skipped here), behind a confirmation since it discards
     * a run in progress.
     */
    bindRestartButton() {
        const button = this.page.querySelector(".btn-restart-ingame");
        if (!button) return;
        button.addEventListener("click", () => {
            const lang = geoNerdApp.languageManager;
            showConfirmation(
                lang.t("settings.confirmTitle"),
                lang.t("settings.confirmMessage"),
                () => { this.resetGame(); this.startGame(); }
            );
        });
    }

    saveStats(score) {
        // Capture the previous best BEFORE overwriting it, so the end screen can
        // show a "new record" badge with the delta.
        const best = parseInt(StorageService.load(`${this.gameId}.best`, 0));
        this.previousBest = best;
        this.isNewBest = score > best;
        if (score > best) {
            StorageService.save(`${this.gameId}.best`, score.toString());
        }
        if (score > 0 && score >= best) {
            this._saveBestRun(score, this.errors.length);
        }
        ScoreUtils.saveStats(this.gameId, score);
        // Push immédiat (pas de debounce) : le lien "classement" affiché juste après
        // est cliquable avant qu'un push différé n'ait lieu.
        AccountSync.push();
    }

    _saveBestRun(score, errors) {
        // `duration: null` par construction (le chrono est la règle du mode, pas une
        // performance) ; `recordedAt` date le record, comme pour les modes à vies.
        const candidate = { score, errors, duration: null, recordedAt: Date.now() };
        saveWeekRun(this.gameId, candidate);
        const existing = StorageService.load(`${this.gameId}.bestRun`);
        if (!existing) { StorageService.save(`${this.gameId}.bestRun`, candidate); return; }
        if (candidate.score !== existing.score) {
            if (candidate.score > existing.score) StorageService.save(`${this.gameId}.bestRun`, candidate);
            return;
        }
        const ea = errors ?? Infinity, eb = existing.errors ?? Infinity;
        if (ea < eb) StorageService.save(`${this.gameId}.bestRun`, candidate);
    }

    nextQuestion() {
        if (!this.gameActive) return;
        this.isAnswering = false;

        if (this.poolIndex >= this.pool.length) {
            this.pool = ArrayUtils.shuffle([...geoNerdApp.countries]);
            this.poolIndex = 0;
            this.preloadPoolFlags();
        }
        this.rightAnswer = this.pool[this.poolIndex++];

        let proposals = [this.buildProposal(this.rightAnswer)];
        while (proposals.length < 4) {
            const country = geoNerdApp.countries[Math.floor(Math.random() * geoNerdApp.countries.length)];
            if (!proposals.find(p => p.code === country.code)) {
                proposals.push(this.buildProposal(country));
            }
        }
        ArrayUtils.shuffle(proposals);

        const codes = this.getQuestionFlagCodes(this.rightAnswer);
        const token = this.questionToken = (this.questionToken || 0) + 1;
        if (!FlagPreloader.isActive() || FlagPreloader.areLoaded(codes)) {
            this.renderTurn(proposals);
            return;
        }
        this.waitingForFlag = true;
        this.flagContainer.innerHTML = '<div class="flag-loader"></div>';
        this.flagContainer.style.opacity = "1";
        this.answerContainer.innerHTML = "";
        FlagPreloader.load(codes).then(() => {
            if (token !== this.questionToken) return;
            this.waitingForFlag = false;
            if (this.gameActive) this.renderTurn(proposals);
        });
    }

    /** Affiche la question tirée par `nextQuestion()` et branche les réponses. */
    renderTurn(proposals) {
        this.flagContainer.innerHTML = this.renderQuestion(this.rightAnswer);
        this.answerContainer.innerHTML = "";

        proposals.forEach(proposal => {
            this.answerContainer.insertAdjacentHTML("beforeend",
                `<button type="button" class="country" data-country-code="${proposal.code}">${this.getAnswerLabel(proposal)}</button>`
            );
        });

        this.answerContainer.style.opacity = "1";
        this.flagContainer.style.opacity = "1";
        this.answerContainer.style.pointerEvents = "initial";

        // Raccourcis 1–4 : posés une fois pour la partie, ils relisent les boutons
        // à chaque frappe. C'est le mode où gagner une demi-seconde par question
        // compte le plus.
        if (!this.unbindAnswerKeys) this.unbindAnswerKeys = bindAnswerKeys(this.answerContainer);

        this.answerContainer.querySelectorAll(".country").forEach(guessBtn => {
            guessBtn.addEventListener("click", () => {
                if (!this.gameActive || this.isAnswering) return;
                this.isAnswering = true;

                const isCorrect = guessBtn.dataset.countryCode === this.rightAnswer.code;
                if (isCorrect) {
                    SoundManager.correct();
                    FeedbackEffects.confetti(guessBtn);
                    guessBtn.classList.add("valid");
                    this.score++;
                    if (this.config.passportStamp) Passport.stamp(this.rightAnswer.code, this.config.passportStamp);
                    this.updateProgress();
                } else {
                    SoundManager.wrong();
                    FeedbackEffects.shake(this.page.querySelector(".game-container"));
                    guessBtn.classList.add("invalid");
                    this.answerContainer.querySelector(`[data-country-code="${this.rightAnswer.code}"]`)?.classList.add("valid");
                    this.errors.push(this.buildError(guessBtn));
                    if (this.config.passportStamp) ReviewDeck.recordMiss(this.config.passportStamp, this.rightAnswer.code);
                    this.timeLeft = Math.max(0, this.timeLeft - 2);
                    this.updateTimerDisplay();
                }

                this.answerContainer.style.pointerEvents = "none";

                // La pénalité de -2s peut vider le chrono. On fige la partie tout
                // de suite — plus aucune réponse acceptée, plus de tick — sinon le
                // timer affichait "0" pendant que la question suivante se chargeait
                // et restait jouable jusqu'au tick suivant (~1s). L'écran de fin
                // attend quand même le feedback de l'erreur, comme une transition
                // normale.
                const timeUp = this.timeLeft <= 0;
                if (timeUp) {
                    this.gameActive = false;
                    this.stopTimer();
                }

                setTimeout(() => {
                    gsap.to([this.flagContainer, this.answerContainer], {
                        opacity: 0,
                        duration: 0.3,
                        onComplete: () => (timeUp ? this.endGame() : this.nextQuestion())
                    });
                }, 500);
            });
        });
    }

    buildProposal(country) { return { code: country.code, name: country.name }; }
    renderQuestion(country) { return ""; }
    /** Drapeaux à avoir en cache avant d'afficher la question (les deux Speed Run en montrent un). */
    getQuestionFlagCodes(country) { return [country.code]; }
    getAnswerLabel(proposal) { return proposal.name; }

    /** Error entry pushed on a wrong answer (consumed by `renderErrorItem`). */
    buildError(guessBtn) {
        return {
            countryName: this.rightAnswer.name,
            correctCode: this.rightAnswer.code,
            guessedCode: guessBtn.dataset.countryCode
        };
    }

    renderErrorItem() { return ""; }

    /**
     * Récap des fautes de l'écran de fin — même rendu et même emplacement que
     * dans les modes à vies, pour que la lecture soit la même partout.
     */
    renderErrors(container) {
        const lang = geoNerdApp.languageManager;
        renderErrorsRecap(container, this.errors, err => this.renderErrorItem(err), lang.t("game.yourMistakes"));
    }

    /**
     * Share the run via the shared clipboard helper, driven by `this.config`
     * ({ gameNameKey, modeLabelKey, slug }). A 🏅 is appended to the score when
     * the run set a new personal best.
     */
    initShare() {
        if (!this.config.gameNameKey) return;
        const { gameNameKey, modeLabelKey, slug } = this.config;
        this.page.querySelectorAll(".btn-share").forEach(btn => {
            btn.addEventListener("click", () => {
                const lang = geoNerdApp.languageManager;
                const scoreText = (this.isNewBest && this.previousBest > 0) ? `${this.score} 🏅` : `${this.score}`;
                geoNerdApp.shareResult(btn, lang.t(gameNameKey), lang.t(modeLabelKey), scoreText, "🟪", slug);
            });
        });
    }

    /**
     * Render the end-of-run score (raw, no denominator — "speed-run" class) and,
     * when the player beat a previous record, a celebratory "+N" badge + confetti.
     */
    renderEndGameStats(container) {
        if (!this.config.modeLabelKey) return;
        const lang = geoNerdApp.languageManager;
        const mode = lang.t(this.config.modeLabelKey);
        const modeLabel = this.config.gameNameKey ? `${lang.t(this.config.gameNameKey)} · ${mode}` : mode;
        ScoreUtils.renderEndGameResults(container, this.gameId, modeLabel, this.score, this.score, "speed-run");
        if (this.isNewBest && this.score >= NUDGE_MIN_SCORE) renderAccountNudge(container, "record");
        else container.querySelector(".account-nudge")?.remove();

        const stale = container.querySelector(".pb-badge");
        if (stale) stale.remove();

        if (this.isNewBest && this.previousBest > 0) {
            const badgeHtml = `<div class="pb-badge">🏆 ${lang.t("game.newBest")} +${this.score - this.previousBest}</div>`;
            const messageP = container.querySelector("p");
            if (messageP) messageP.insertAdjacentHTML("afterend", badgeHtml);
            else container.insertAdjacentHTML("afterbegin", badgeHtml);

            // Fire confetti once the end screen is actually shown (it gets the
            // `show` class right after this method returns), so particles
            // originate from the visible badge rather than a hidden element.
            setTimeout(() => {
                const badge = container.querySelector(".pb-badge");
                if (badge) FeedbackEffects.confetti(badge);
            }, 60);
        }
    }
}
